/**
 * La sede más cercana a unas coordenadas, calculada en el navegador.
 *
 * Es la salida de emergencia del flujo de domicilios: se usa justo cuando la
 * cobertura no contestó, así que no puede depender de la red. No hace falta:
 * `instance.suppliers_delivery` ya viaja con `latitude` y `longitude` en la
 * configuración de la cuenta.
 *
 * Hasta ahora esta decisión era `suppliers_delivery.find(active_delivery)`: la
 * primera sede abierta **en el orden de la cuenta**. Para Pizza House Gourmet
 * eso era siempre Unicentro, a 6,9 km de un cliente que tenía otra sede a 130 m.
 */
const EARTH_RADIUS_KM = 6371;

const square = value => value * value;

const toRadians = degrees => (degrees * Math.PI) / 180;

const hasCoordinates = point =>
  !!point &&
  Number.isFinite(Number(point.latitude)) &&
  Number.isFinite(Number(point.longitude));

/**
 * Haversine. Sobra para lo único que se hace con ella: comparar entre sí las
 * sedes de una misma ciudad. Devuelve `null` cuando falta alguna coordenada,
 * porque un `NaN` comparándose con `<` gana o pierde según el orden del arreglo.
 */
export const distanceInKm = (from, to) => {
  if (!hasCoordinates(from) || !hasCoordinates(to)) return null;

  const fromLatitude = toRadians(Number(from.latitude));
  const toLatitude = toRadians(Number(to.latitude));
  const latitudeDelta = toLatitude - fromLatitude;
  const longitudeDelta = toRadians(
    Number(to.longitude) - Number(from.longitude)
  );

  const a =
    square(Math.sin(latitudeDelta / 2)) +
    Math.cos(fromLatitude) *
      Math.cos(toLatitude) *
      square(Math.sin(longitudeDelta / 2));

  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(a));
};

export const openDeliverySuppliers = suppliers =>
  (suppliers || []).filter(supplier => supplier.active_delivery === true);

/**
 * El orden de la cuenta. No es una apuesta: es lo único que queda cuando no hay
 * coordenadas con qué comparar, y por eso quien lo use tiene que registrarlo
 * distinto de una cercanía calculada de verdad.
 */
export const firstOpenSupplier = suppliers =>
  openDeliverySuppliers(suppliers)[0] || null;

const nearestOf = (suppliers, coordinates) =>
  (suppliers || []).reduce((closest, supplier) => {
    const distance = distanceInKm(coordinates, supplier);
    if (distance === null) return closest;
    if (!closest || distance < closest.distance) return { supplier, distance };

    return closest;
  }, null);

/**
 * La sede abierta más cercana, como `{ supplier, distance }`.
 *
 * Devuelve `null` —y no la primera de la lista— cuando no hay coordenadas o
 * ninguna sede abierta las tiene. Que no se pueda calcular la cercanía es una
 * respuesta, y quien llama tiene que poder distinguirla de un cálculo real.
 */
export const nearestSupplier = (suppliers, coordinates) => {
  if (!hasCoordinates(coordinates)) return null;

  return nearestOf(openDeliverySuppliers(suppliers), coordinates);
};

/**
 * La más cercana sin exigir que esté recibiendo domicilios.
 *
 * Existe para el caso en que ninguna lo está: ahí "abierta" dejó de ser un
 * filtro y la cercanía sigue siendo la mejor manera de elegir cuál menú
 * mirar.
 */
export const nearestAnySupplier = (suppliers, coordinates) => {
  if (!hasCoordinates(coordinates)) return null;

  return nearestOf(suppliers, coordinates);
};
